import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import { current } from "immer";
import type { OfferDetailResponse } from "@codway-dev/shared";
import { OfferService } from "../services/offer.service";
import { isDeepEqual } from "../utils/deep-equal";
import {
  getDefaultBundleEditorState,
  getDefaultBundleEditorItem,
  getDefaultBundleEditorOption,
  getDefaultBundleEditorGroup,
  offerToState,
  stateToCreateRequest,
  stateToUpdateRequest,
  type BundleEditorState,
  type BundleEditorItem,
  type BundleEditorGroup,
  type BundleEditorOption,
  type BundleType,
} from "./bundle-editor.mapping";

// ─── Types ────────────────────────────────────────────────────────────────────

interface BundleEditorStoreState {
  config: BundleEditorState;
  offerId: string | null;
  originalConfig: OfferDetailResponse["config"] | null;
  initialConfig: BundleEditorState | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface BundleEditorActions {
  initNew: () => void;
  // Chunk 4 wires these against OfferService.
  loadFromBackend: (id: string) => Promise<void>;
  save: () => Promise<string>;

  updateField: <K extends keyof BundleEditorState>(
    key: K,
    value: BundleEditorState[K]
  ) => void;

  // Bundle type — switching clears items/groups (the two shapes are
  // not transformable). UI is expected to confirm with the merchant
  // before calling this when items/groups have content.
  setBundleType: (type: BundleType) => void;

  // Fixed-bundle item ops. `addItems` is the primary entry point —
  // the resource picker hands us N variants in one call, and a single
  // atomic update is cheaper than N store mutations (one re-render
  // instead of N) plus avoids partial-state flickers in subscribers.
  addItems: (items: Partial<Omit<BundleEditorItem, "_tempId">>[]) => void;
  removeItem: (index: number) => void;
  updateItem: <K extends keyof BundleEditorItem>(
    index: number,
    key: K,
    value: BundleEditorItem[K]
  ) => void;
  reorderItems: (fromIndex: number, toIndex: number) => void;

  // Configurable-bundle group ops
  addGroup: () => void;
  removeGroup: (groupIndex: number) => void;
  updateGroup: <K extends keyof BundleEditorGroup>(
    groupIndex: number,
    key: K,
    value: BundleEditorGroup[K]
  ) => void;
  reorderGroups: (fromIndex: number, toIndex: number) => void;

  // Configurable-bundle option ops. Plural by design — matches the
  // picker's batch return and lands all options in a single atomic
  // update.
  addOptions: (
    groupIndex: number,
    options: Partial<Omit<BundleEditorOption, "_tempId">>[]
  ) => void;
  removeOption: (groupIndex: number, optionIndex: number) => void;
  updateOption: <K extends keyof BundleEditorOption>(
    groupIndex: number,
    optionIndex: number,
    key: K,
    value: BundleEditorOption[K]
  ) => void;

  discardChanges: () => void;
  reset: () => void;
}

type BundleEditorStore = BundleEditorStoreState & BundleEditorActions;

// ─── Selectors ───────────────────────────────────────────────────────────────

export const selectBundleIsDirty = (state: BundleEditorStore) => {
  if (state.initialConfig === null) return false;
  return !isDeepEqual(state.config, state.initialConfig);
};

// ─── Store ───────────────────────────────────────────────────────────────────

const getInitialState = (): BundleEditorStoreState => ({
  config: getDefaultBundleEditorState(),
  offerId: null,
  originalConfig: null,
  initialConfig: null,
  isLoading: false,
  isSaving: false,
  error: null,
});

export const useBundleEditorStore = create<BundleEditorStore>()(
  devtools(
    immer((set, get) => ({
      ...getInitialState(),

      initNew: () =>
        set((state) => {
          const fresh = getDefaultBundleEditorState();
          state.config = fresh;
          state.offerId = null;
          state.originalConfig = null;
          state.initialConfig = fresh;
          state.isLoading = false;
          state.isSaving = false;
          state.error = null;
        }),

      loadFromBackend: async (id) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const offer = await OfferService.get(id);
          const hydrated = offerToState(offer);
          set((state) => {
            state.config = hydrated;
            state.offerId = offer.id;
            state.originalConfig = offer.config;
            state.initialConfig = hydrated;
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error
                ? err.message
                : "No se pudo cargar la oferta";
            state.isLoading = false;
          });
          throw err;
        }
      },

      save: async () => {
        set((state) => {
          state.isSaving = true;
          state.error = null;
        });

        try {
          const s = get();
          const saved =
            s.offerId === null
              ? await OfferService.create(stateToCreateRequest(s.config))
              : await OfferService.update(
                  s.offerId,
                  stateToUpdateRequest(s.config, s.originalConfig)
                );

          set((state) => {
            state.offerId = saved.id;
            // Refresh BOTH snapshots from the server response so a
            // subsequent save merges against the post-save config, not
            // the stale pre-save one. Without this, edit-save-edit-save
            // without a reload silently regresses fields the server
            // mutated (e.g. defaults applied on create).
            state.originalConfig = saved.config;
            state.initialConfig = current(state.config);
            state.isSaving = false;
          });

          return saved.id;
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Error al guardar la oferta";
          set((state) => {
            state.isSaving = false;
            state.error = message;
          });
          throw new Error(message);
        }
      },

      updateField: (key, value) =>
        set((state) => {
          state.config[key] = value as never;
        }),

      setBundleType: (type) =>
        set((state) => {
          if (state.config.bundleType === type) return;
          state.config.bundleType = type;
          // Hard reset: the two shapes can't be interchanged cleanly
          // (items have quantity + per-item price; group options don't).
          // UI must confirm with the merchant before this fires.
          state.config.items = [];
          state.config.groups = [];
        }),

      // ── Fixed-bundle item ops ────────────────────────────────────────────
      addItems: (items) =>
        set((state) => {
          // Dedupe by variantId in a single pass — handles both
          // duplicates against existing items AND duplicates within the
          // incoming batch (the picker can return the same variant twice
          // if the merchant double-selects it). One atomic mutation =
          // one re-render for subscribers regardless of batch size.
          for (const item of items) {
            const incomingVariantId = item?.variantId;
            if (incomingVariantId) {
              const existing = state.config.items.find(
                (i) => i.variantId === incomingVariantId
              );
              if (existing) {
                existing.quantity += item?.quantity ?? 1;
                continue;
              }
            }
            state.config.items.push({
              ...getDefaultBundleEditorItem(),
              ...item,
            });
          }
        }),

      removeItem: (index) =>
        set((state) => {
          if (index < 0 || index >= state.config.items.length) return;
          state.config.items.splice(index, 1);
        }),

      updateItem: (index, key, value) =>
        set((state) => {
          if (index < 0 || index >= state.config.items.length) return;
          state.config.items[index][key] = value as never;
        }),

      reorderItems: (fromIndex, toIndex) =>
        set((state) => {
          const items = state.config.items;
          if (
            fromIndex < 0 ||
            toIndex < 0 ||
            fromIndex >= items.length ||
            toIndex >= items.length ||
            fromIndex === toIndex
          ) {
            return;
          }
          const [moved] = items.splice(fromIndex, 1);
          items.splice(toIndex, 0, moved);
        }),

      // ── Configurable-bundle group ops ────────────────────────────────────
      addGroup: () =>
        set((state) => {
          state.config.groups.push(getDefaultBundleEditorGroup());
        }),

      removeGroup: (groupIndex) =>
        set((state) => {
          if (groupIndex < 0 || groupIndex >= state.config.groups.length)
            return;
          state.config.groups.splice(groupIndex, 1);
        }),

      updateGroup: (groupIndex, key, value) =>
        set((state) => {
          if (groupIndex < 0 || groupIndex >= state.config.groups.length)
            return;
          state.config.groups[groupIndex][key] = value as never;
        }),

      reorderGroups: (fromIndex, toIndex) =>
        set((state) => {
          const groups = state.config.groups;
          if (
            fromIndex < 0 ||
            toIndex < 0 ||
            fromIndex >= groups.length ||
            toIndex >= groups.length ||
            fromIndex === toIndex
          ) {
            return;
          }
          const [moved] = groups.splice(fromIndex, 1);
          groups.splice(toIndex, 0, moved);
        }),

      // ── Configurable-bundle option ops ───────────────────────────────────
      addOptions: (groupIndex, options) =>
        set((state) => {
          if (groupIndex < 0 || groupIndex >= state.config.groups.length)
            return;
          const group = state.config.groups[groupIndex];
          // One atomic mutation for the whole batch — one re-render
          // regardless of how many options the picker returned.
          for (const option of options) {
            group.options.push({
              ...getDefaultBundleEditorOption(),
              ...option,
            });
          }
        }),

      removeOption: (groupIndex, optionIndex) =>
        set((state) => {
          const group = state.config.groups[groupIndex];
          if (!group) return;
          if (optionIndex < 0 || optionIndex >= group.options.length) return;
          group.options.splice(optionIndex, 1);
        }),

      updateOption: (groupIndex, optionIndex, key, value) =>
        set((state) => {
          const group = state.config.groups[groupIndex];
          if (!group) return;
          if (optionIndex < 0 || optionIndex >= group.options.length) return;
          group.options[optionIndex][key] = value as never;
        }),

      discardChanges: () =>
        set((state) => {
          if (!state.initialConfig) return;
          state.config = state.initialConfig;
        }),

      reset: () =>
        set((state) => {
          Object.assign(state, getInitialState());
        }),
    })),
    { name: "BundleEditorStore" }
  )
);
